'use client';
import React, {useCallback, useEffect, useState} from "react";
import {IScheduleEntryAssignments} from "../../core/interfaces";
import Loader from "../../core/components/Loader";
import {Link, useParams} from "react-router-dom";
import {ArrowLeft} from "@mui/icons-material";
import {getScheduleEntryAssignments} from "../../services/readingPlans";

const ScheduleIntervalDetailsPage: React.FC = (): JSX.Element => {
    const {iGroupId, planId, scheduleEntryId} = useParams()

    const [loading, setLoading] = useState<boolean>(false)
    const [data, setData] = useState<IScheduleEntryAssignments | null>(null)

    const getData = useCallback(async () => {
        try {
            setLoading(true)
            const response = await getScheduleEntryAssignments(scheduleEntryId)
            setData(response.data)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }, [scheduleEntryId])

    useEffect(() => {
        getData()
    }, [getData])

    return (
        <div style={{padding: 20, backgroundColor: '#F8F8F8', position: 'relative', minHeight: '100vh'}}>
            {loading && <Loader />}

            <div style={{alignItems: 'center', display: 'flex', marginBottom: 20, gap: 12}}>
                <Link to={`/plans/institution-group/${iGroupId}/${planId}/edit`}>
                    <ArrowLeft style={{backgroundColor: 'black', color: 'white', padding: '0px', width: '20px', height: '20px', textAlign: 'center', lineHeight: '1.1', borderRadius: '10px'}}/>
                </Link>
                <h2 style={{margin: 0}}>
                    Interval #{data?.index} — Assignments
                </h2>
                {data && (
                    <span style={{fontSize: 14, color: '#666'}}>
                        {new Date(data.startDate).toLocaleDateString()} → {new Date(data.endDate).toLocaleDateString()}
                    </span>
                )}
            </div>

            {data && (
                <div style={{overflowX: 'auto'}}>
                    <table style={{width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden'}}>
                        <thead>
                            <tr style={{background: '#f0f0f0', textAlign: 'left'}}>
                                <th style={thStyle}>Kid name</th>
                                <th style={thStyle}>Parent</th>
                                <th style={thStyle}>Group</th>
                                <th style={thStyle}>Books</th>
                                <th style={thStyle}>Group status</th>
                                <th style={thStyle}>Additional book</th>
                                <th style={thStyle}>Additional status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.assignments.length === 0 && (
                                <tr>
                                    <td colSpan={7} style={{...tdStyle, textAlign: 'center', color: '#888'}}>
                                        No assignments for this interval.
                                    </td>
                                </tr>
                            )}
                            {data.assignments.map(a => (
                                <tr key={a.id as string} style={{borderBottom: '1px solid #eee'}}>
                                    <td style={tdStyle}>{a.kidName || '—'}</td>
                                    <td style={tdStyle}>{a.firstName} {a.lastName}</td>
                                    <td style={tdStyle}>
                                        <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                            <span style={{
                                                display: 'inline-block',
                                                width: 14,
                                                height: 14,
                                                borderRadius: '50%',
                                                backgroundColor: a.groupColor || '#ccc',
                                                border: '1px solid #aaa',
                                                flexShrink: 0,
                                            }} />
                                            <span>#{a.groupIndex}</span>
                                        </div>
                                    </td>
                                    <td style={tdStyle}>
                                        {a.bookNames?.map((name, i) => (
                                            <div key={i} style={{fontSize: 13}}>• {name}</div>
                                        ))}
                                    </td>
                                    <td style={tdStyle}>
                                        <StatusBadge status={a.groupStatus} />
                                    </td>
                                    <td style={tdStyle}>
                                        {a.additionalBookName ? (
                                            <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                                {a.additionalBookColor && (
                                                    <span style={{
                                                        display: 'inline-block',
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor: a.additionalBookColor,
                                                        border: '1px solid #aaa',
                                                        flexShrink: 0,
                                                    }} />
                                                )}
                                                <span style={{fontSize: 13}}>{a.additionalBookName}</span>
                                            </div>
                                        ) : '—'}
                                    </td>
                                    <td style={tdStyle}>
                                        {a.additionalBookStatus ? <StatusBadge status={a.additionalBookStatus} /> : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

const thStyle: React.CSSProperties = {
    padding: '10px 14px',
    fontWeight: 600,
    fontSize: 13,
    borderBottom: '2px solid #ddd',
}

const tdStyle: React.CSSProperties = {
    padding: '10px 14px',
    fontSize: 14,
    verticalAlign: 'top',
}

const statusColors: Record<string, {bg: string; color: string}> = {
    NOT_RECEIVED: {bg: '#fff3e0', color: '#e65100'},
    RECEIVED: {bg: '#e8f5e9', color: '#2e7d32'},
    RETURNED: {bg: '#e3f2fd', color: '#1565c0'},
    EXCHANGED: {bg: '#f3e5f5', color: '#6a1b9a'},
}

const StatusBadge: React.FC<{status: string}> = ({status}) => {
    const style = statusColors[status] || {bg: '#f5f5f5', color: '#333'}
    return (
        <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 10,
            fontSize: 12,
            fontWeight: 500,
            background: style.bg,
            color: style.color,
        }}>
            {status?.replace(/_/g, ' ') || '—'}
        </span>
    )
}

export default ScheduleIntervalDetailsPage
